import { AxiosError, AxiosResponse } from "axios";
import {
    addMethod,
    boolean,
    mixed,
    setLocale,
    number as yupNumber,
    string as yupString,
} from "yup";

import { create } from "@api/api";

import { getPrefixOfFIeld } from "@utils/address/address";
import { getApiBaseUrl, getBaseStore } from "@utils/configuration/aem";

import * as NeverbounceService from "@services/neverbounce.service";
import {
    doVatCheckBe,
    doVatCheckDe,
    doVatCheckFr,
    doVatCheckLu,
    doVatCheckNl,
    doVatCheckOther,
} from "@services/vatCalculation.service";
import { checkCountryZipCode } from "@services/zipCode.service";

import { CustomerModel } from "@redux/modules/api/data/customer/@types/customer.model";
import { translateWithoutContext } from "@redux/modules/value/translations/translations.helpers";
import { update as updateVatInfo } from "@redux/modules/value/vatInfo/vatInfo.module";
import { getVatInfoFor } from "@redux/modules/value/vatInfo/vatInfo.selectors";

import { hasLocalStorageTimestamp } from "./bonusCard";
import { isDefined } from "./helpers";
import { string } from "./validations/general";

type VATValidationResponse = {
    countryCode?: string;
    city?: string;
    zipcode?: string;
    street?: string;
    number?: string;
    box?: string | null;
    companyName?: string;
    valid: boolean;
    vatLiable: boolean;
};

export const patterns = {
    phone: new RegExp(/^\d{8,15}$/i),
    bonusCard: new RegExp(/^(2062|2499)(\d{9})$/),
    promotionCode: new RegExp(/^[a-zA-Z0-9-()]+$/),
    oldBonusCard: new RegExp(/^2062(\d{9})$/),
    // email regex from Yup validation pattern
    email: new RegExp(
        // eslint-disable-next-line no-control-regex,no-useless-escape
        /^(?:[a-z0-9!$'*+=_`~-]+(?:\.[a-z0-9!$'*+=_`~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/i,
    ),
};

export const BONUSCARD_DISCOUNT_PREFIX = "26555-";

setLocale({
    mixed: {
        required: ({ label, path }) => {
            return translateWithoutContext(
                "form.validation.input.required",
                label || path,
            );
        },
    },
    string: {
        // @ts-ignore todo: not supported?
        required: ({ label, path }: any) =>
            translateWithoutContext(
                "form.validation.input.required",
                label || path,
            ),
        bonusCard: ({ label, path }: any) =>
            translateWithoutContext(
                "form.validation.bonusCard.invalid",
                label || path,
            ),
        email: ({ label, path }) =>
            translateWithoutContext(
                "form.validation.input.string.email",
                label || path,
            ),
        min: ({ label, min, path }) =>
            translateWithoutContext(
                "form.validation.input.string.min",
                label || path,
                min,
            ),
        max: ({ label, max, path }) =>
            translateWithoutContext(
                "form.validation.input.string.max",
                label || path,
                max,
            ),
        phone: ({ label, path }: any) =>
            translateWithoutContext(
                "form.validation.phone.invalid",
                label || path,
            ),
    },
    number: {
        min: ({ label, min, path }) =>
            translateWithoutContext(
                "form.validation.input.number.min",
                label || path,
                min,
            ),
        max: ({ label, max, path }) =>
            translateWithoutContext(
                "form.validation.input.number.max",
                label || path,
                max,
            ),
        integer: ({ label, path }) =>
            translateWithoutContext(
                "form.validation.input.number.integer",
                label || path,
            ),
    },
});

/**
 * VAT validation Rule
 */
const VATValidationAPI = create("", true);
addMethod(mixed, "vatCalculation", function () {
    return this.test("vatCalculation", { exclusive: true }, function (value) {
        const {
            createError,
            options: { context: { values: formValues = {} } = {} },
        } = this;
        const vatNrPrefix = formValues?.vatNrPrefix || "";

        let errorMessage = translateWithoutContext(
            "general.form.input.validation.vat.invalid",
        );

        const parsedValue = value.replaceAll("_", "");

        return new Promise((resolve, reject) => {
            let rValue;
            switch (vatNrPrefix.toLowerCase()) {
                case "":
                case null:
                    rValue = false;
                    break;
                case "be":
                    rValue = doVatCheckBe(parsedValue);
                    break;
                case "nl":
                    rValue = doVatCheckNl(parsedValue);
                    break;
                case "fr":
                    rValue = doVatCheckFr(parsedValue);
                    break;
                case "de":
                    rValue = doVatCheckDe(parsedValue);
                    break;
                case "lu":
                    rValue = doVatCheckLu(parsedValue);
                    break;
                default:
                    rValue = doVatCheckOther(parsedValue);
            }

            if (rValue) {
                const store = window.storeManager;
                const vatInfo = getVatInfoFor(value)(store.getState());

                if (
                    vatInfo &&
                    vatInfo.vatNr === parsedValue &&
                    vatNrPrefix === vatInfo.vatNrPrefix
                ) {
                    return resolve(true);
                }

                store.dispatch(updateVatInfo({ isLoading: true }));

                VATValidationAPI.request<
                    any,
                    AxiosResponse<VATValidationResponse>,
                    AxiosResponse<VATValidationResponse>
                >({
                    method: "get",
                    url: `${getApiBaseUrl()}/i18nservice/${getBaseStore()}/vat/${vatNrPrefix}/${parsedValue}`,
                    data: {} as any,
                    headers: {
                        cache: "force-cache",
                        timeout: 5000,
                        mode: "cors",
                    },
                })
                    .then(response => {
                        if (response?.data?.valid) {
                            // dispatch to store
                            const vatInfo = {
                                vatNrPrefix,
                                vatNr: value,
                                ...(response?.data || {}),
                            };
                            store.dispatch(
                                updateVatInfo({ data: { ...vatInfo } }),
                            );
                            resolve(true);
                        } else {
                            reject(
                                createError({
                                    message: errorMessage,
                                }),
                            );
                        }
                    })
                    .catch((error: any) => {
                        const data =
                            error.response &&
                            typeof error.response.data === "object"
                                ? error.response.data?.message
                                : error.response?.data;
                        if (data) {
                            errorMessage = translateWithoutContext(
                                "apiMessages.title." + data,
                            );

                            reject(
                                createError({
                                    message: errorMessage,
                                }),
                            );
                        }

                        resolve(true);
                    })
                    .finally(() => {
                        store.dispatch(
                            updateVatInfo({
                                isLoading: false,
                            }),
                        );
                    });
            } else {
                reject(
                    createError({
                        message: errorMessage,
                    }),
                );
            }
        });
    });
});

/**
 * ZipCode validation Rule
 */
addMethod(mixed, "zipCode", function () {
    return this.test("zipCode", function (value) {
        const {
            options: { context: { values: formValues = {} } = {} },
        } = this;
        const prefix = getPrefixOfFIeld(this.path);
        const country = formValues[`${prefix}country`] || null;
        const errorMessage = translateWithoutContext(
            "general.form.input.validation.zipCode.invalid",
        );

        return (
            (country && checkCountryZipCode(country, value)) ||
            this.createError({ path: this.path, message: errorMessage })
        );
    });
});

addMethod(
    yupString,
    "verifyBonusCardPresence",
    function (customer: CustomerModel | null) {
        return this.test("verifyBonusCardPresence", function (value = "") {
            const matchesPrefix = value?.startsWith(BONUSCARD_DISCOUNT_PREFIX);
            if (matchesPrefix && !customer?.bonusCardNumber) {
                let errorMessage = translateWithoutContext(
                    `general.form.input.validation.verifyBonusCardPresence`,
                );
                return this.createError({ message: errorMessage });
            }
            return true;
        });
    },
);

addMethod(yupString, "verifyBonusCardDiscount", function () {
    return this.test("verifyBonusCardDiscount", function (value = "") {
        const matchesPrefix = value?.startsWith(BONUSCARD_DISCOUNT_PREFIX);
        if (matchesPrefix) {
            let errorMessage = translateWithoutContext(
                `general.form.input.validation.verifyBonusCardDiscount`,
            );
            return this.createError({ message: errorMessage });
        }
        return true;
    });
});

/**
 * BPost address check
 */
addMethod(yupString, "bPostCheck", function () {
    return this.test("bPostCheck", function () {
        const {
            options: { context: { values: formValues = {} } = {} },
        } = this;
        const missingElements = [];
        const prefix = getPrefixOfFIeld(this.path);

        if (!formValues[`${prefix}selected`]) {
            return this.createError({
                message: translateWithoutContext(
                    "general.form.input.validation.noAddressSelected",
                ),
            });
        }

        // Check which elements are missing
        if (!formValues[`${prefix}street`]) {
            missingElements.push(
                translateWithoutContext("general.form.input.validation.street"),
            );
        }
        if (!formValues[`${prefix}number`]) {
            missingElements.push(
                translateWithoutContext("general.form.input.validation.number"),
            );
        }
        if (!formValues[`${prefix}zipCode`]) {
            missingElements.push(
                translateWithoutContext(
                    "general.form.input.validation.zipCode",
                ),
            );
        }
        if (!formValues[`${prefix}city`]) {
            missingElements.push(
                translateWithoutContext("general.form.input.validation.city"),
            );
        }
        if (!formValues[`${prefix}box`] && !formValues[`${prefix}isComplete`]) {
            missingElements.push(
                translateWithoutContext("general.form.input.validation.box"),
            );
        }

        return (
            missingElements.length === 0 ||
            this.createError({
                message: translateWithoutContext(
                    "general.form.input.validation.address.incomplete",
                    missingElements.join(", "),
                ),
            })
        );
    });
});

/**
 * Bonus card validation Rule
 */
const BonusCardAPI = create("", true);
addMethod(yupString, "bonusCard", function (required, onlyOld = false) {
    return this.test("bonusCard", { exclusive: true }, function (value) {
        const createBonusCardInvalidError = () =>
            translateWithoutContext(
                "general.form.input.validation.bonusCard.invalid",
            );

        return new Promise((resolve, reject) => {
            if (!required && !value) {
                return resolve(true);
            }

            if (
                !isDefined(value) ||
                !(onlyOld
                    ? patterns.oldBonusCard.test(value)
                    : patterns.bonusCard.test(value))
            ) {
                return reject(
                    this.createError({
                        message: createBonusCardInvalidError(),
                    }),
                );
            }

            BonusCardAPI.request({
                method: "get",
                url: `${getApiBaseUrl()}/customerservice/${getBaseStore()}/bonuscards/${value}`,
                data: {},
                headers: {
                    cache: "force-cache",
                    timeout: 5000,
                    mode: "cors",
                },
            })
                .then(() => resolve(true))
                .catch((error: AxiosError) => {
                    const errorCode = error.response?.data;
                    let errorMessage = errorCode
                        ? translateWithoutContext(
                              `general.form.input.validation.bonusCard.${errorCode}`,
                          )
                        : undefined;

                    errorMessage ||= createBonusCardInvalidError();

                    reject(
                        this.createError({
                            message: errorMessage,
                        }),
                    );
                });
        });
    });
});

/**
 * Bonus card validation Rule: format only
 */
addMethod(yupString, "bonusCardFormatOnly", function (required) {
    return this.test(
        "bonusCardFormatOnly",
        { exclusive: true },
        function (value) {
            const errorMessage = translateWithoutContext(
                "general.form.input.validation.bonusCard.invalidformat",
            );
            if (!required && !value) return true;
            // @ts-ignore
            if (patterns.bonusCard.test(value)) {
                return true;
            }
            return this.createError({ message: errorMessage });
        },
    );
});

addMethod(yupString, "promotionCode", function () {
    return this.test("promotionCode", { exclusive: true }, function (value) {
        const errorMessage = translateWithoutContext(
            "general.form.input.validation.promotionCode.invalidformat",
        );
        // @ts-ignore
        if (patterns.promotionCode.test(value)) {
            return true;
        }
        return this.createError({ message: errorMessage });
    });
});

addMethod(yupString, "isEqualPassword", function () {
    return this.test("isEqualPassword", function (value) {
        const {
            createError,
            options: { context: { values: formValues = {} } = {} },
        } = this;

        const errorMessage = translateWithoutContext(
            "general.form.input.validation.passwordRepeat.invalid",
        );
        const passwordValue = formValues.newPassword;

        return (
            (passwordValue && value && passwordValue === value) ||
            createError({ message: errorMessage })
        );
    });
});

addMethod(yupString, "bonusCardRequested", function () {
    return this.test("bonusCardRequested", function (value) {
        const errorMessage = translateWithoutContext(
            "general.form.input.validation.email.bonusCardRequested.invalid",
        );

        const hasNoValue = !!value && !hasLocalStorageTimestamp(value);

        return hasNoValue || this.createError({ message: errorMessage });
    });
});

addMethod(yupNumber, "valutaBetween", function (min, max) {
    return this.test("valutaBetween", function (value) {
        const errorMessage = translateWithoutContext(
            "form.validation.input.valuta.between",
            min,
            max,
        );
        return (
            // @ts-ignore
            (value >= min && value <= max) ||
            this.createError({ message: errorMessage })
        );
    });
});

/**
 Existing email validation
 **/

const checkEmailAxios = create("", true, {
    headers: {},
    validateStatus: status => {
        return status === 404 || (status >= 200 && status < 400);
    },
});

addMethod(yupString, "validateEmail", function (checkExisting) {
    return this.test("validateEmail", function (value) {
        const invalidatedErrorMessage = translateWithoutContext(
            "registerForm.error.invalidatedEmail",
        );
        const existingMailErrorMessage = translateWithoutContext(
            "registerForm.error.existingUser",
        );
        return new Promise((resolve, reject) => {
            const checkExistingEmail = () => {
                if (!checkExisting) {
                    resolve(true);
                    return;
                }
                checkEmailAxios
                    .head(
                        `${getApiBaseUrl()}/customerservice/${getBaseStore()}/customers/${value}`,
                        {
                            cache: {
                                methods: ["head"],
                                ttl: 30000, //ms
                                cachePredicate: {
                                    statusCheck: status =>
                                        status >= 200 && status <= 404,
                                },
                            },
                        },
                    )
                    .then(({ status }) => {
                        if (status === 404) {
                            resolve(true);
                        } else {
                            reject(
                                this.createError({
                                    message: existingMailErrorMessage,
                                }),
                            );
                        }
                    })
                    .catch(() => {
                        return resolve(true);
                    });
            };
            // @ts-ignore
            if (patterns.email.test(value)) {
                // @ts-ignore
                NeverbounceService.validateEmailAddress(value).then(
                    (result: any) => {
                        if (result) {
                            checkExistingEmail();
                        } else {
                            reject(
                                this.createError({
                                    message: invalidatedErrorMessage,
                                }),
                            );
                        }
                    },
                    () => checkExistingEmail(),
                );
            } else {
                reject(
                    this.createError({
                        message: invalidatedErrorMessage,
                    }),
                );
            }
        });
    });
});

// General FirstName validation
export const firstName = string().nullable(true).required().max(35);

export const optionalFirstName = string().nullable(true).max(35);

// Generale lastName validation
export const lastName = string().nullable(true).required().max(35);

export const optionalLastName = string().nullable(true).max(35);

export const contact = string().nullable(true).required().max(100);

// General email validation
export const email = string().nullable(true).required().trim().email();

export const emailWithBonusCardState = string()
    .nullable(true)
    .required()
    .trim()
    .email()
    .bonusCardRequested();

export const verifyBonusCardDiscount = string()
    .nullable(true)
    .verifyBonusCardDiscount();

export const emailWithNB = string()
    .nullable(true)
    .required()
    .trim()
    .email()
    .validateEmail(false);

// General password validation
export const password = string().nullable(true).required().min(8);

// General password repeat validation
export const passwordRepeat = string()
    .nullable(true)
    .required()
    .min(8)
    .isEqualPassword();

// General customerType validation
export const customerType = string();

// General Vat number validation
export const vatNr = mixed()
    .nullable(true)
    .required()
    .when("vatNrPrefix", {
        is: (val: string) =>
            ["BE", "DE", "LU", "FR", "NL"].some(
                v => v.toLowerCase() === val.toLowerCase(),
            ),
        // @ts-ignore
        then: mixed().vatCalculation(),
        // @ts-ignore
        otherwise: string().min(8).max(12).vatCalculation(),
    });

// General CompanyName validation
export const companyName = string().nullable(true).required().max(100);

// General addressing validation
export const addressing = string().nullable(true).max(100);

// General BPost Address line validation
// @ts-ignore
export const addressLine = string().nullable(true).required().bPostCheck();

// General Street validation
export const street = string().nullable(true).required().max(100);

// General Number validation
export const number = string().nullable(true).required().max(5);

// General box validation
export const box = string().nullable(true).max(10);

// General ZipCode validation
// @ts-ignore
export const zipCode = mixed().nullable(true).required().zipCode();

// General City validation
export const city = string().nullable(true).required().max(100);

// General Country validation
export const country = string().nullable(true).required();

// General StoreId validation
export const storeId = string()
    .nullable(true)
    .required(() =>
        translateWithoutContext("form.validation.input.storeId.required"),
    );

export const orderNumber = string().nullable(true).max(100);

// General GiftCard validation
export const giftCard = string()
    .nullable(true)
    .required(() =>
        translateWithoutContext(
            "checkout.paymentMethods.giftCard.validation.input.required",
        ),
    )
    .min(10)
    .max(20);

export const giftCardCheckout = (customer?: CustomerModel | null) =>
    giftCard.verifyBonusCardPresence(customer);

export const giftCardQuantity = yupNumber()
    .transform(value => (isNaN(value) ? undefined : value))
    .nullable(true)
    .required()
    .integer()
    .min(1)
    .max(999);

export const giftCardAmountInEuros = yupNumber()
    .transform(value => (isNaN(value) ? undefined : value))
    .nullable(true)
    .required()
    .integer()
    .valutaBetween(5, 2500);

// General bonusCardAvailable validation
export const bonusCardAvailable = string().nullable(true);

export const bonusCard = string()
    .nullable(true)
    .required()
    .bonusCard(true, false);

export const oldBonusCard = string()
    .nullable(true)
    .required()
    .bonusCard(true, true);

export const optionalBonusCard = string()
    .nullable(true)
    .bonusCard(false, false);

export const bonusCardFormatOnly = string()
    .nullable(true)
    .required()
    .bonusCardFormatOnly(true);

export const checkExistingEmail = string()
    .nullable(true)
    .required()
    .trim()
    .email()
    .validateEmail(true);

export const language = string().nullable(true).required();

export const categories = string().nullable(true).required();

export const digits = yupNumber()
    .transform(value => (isNaN(value) ? undefined : value))
    .nullable(true)
    .required()
    .integer();

export const wishlistName = string().nullable(true).required().max(25);

export const optionalWishlistName = string().nullable(true).max(25);

export const message = (maxLength: number) =>
    string().nullable(true).required().max(maxLength);

export const optionalMessage = (maxLength: number) =>
    string().nullable(true).max(maxLength);

export const ageCondition = boolean().oneOf([true], () =>
    translateWithoutContext(
        "checkout.customerDetails.ageCondition.validation.input.required",
    ),
);
export const contestCondition = boolean().oneOf([true], () =>
    translateWithoutContext(
        "checkout.customerDetails.contestCondition.validation.input.required",
    ),
);
export const partnerCondition = boolean().oneOf([true], () =>
    translateWithoutContext(
        "checkout.customerDetails.partnerCondition.validation.input.required",
    ),
);

export const promotionCode = string()
    .min(2, ({ min }) =>
        translateWithoutContext(
            "general.form.input.validation.promotionCode.min",
            min,
        ),
    )
    .max(30, ({ max }) =>
        translateWithoutContext(
            "general.form.input.validation.promotionCode.max",
            max,
        ),
    )
    .nullable(true)
    .required()
    .promotionCode();
